import React, {
    useState,
    useLayoutEffect,
    useCallback,
    forwardRef,
} from 'react';
import {
    TextTarget,
    TargetType,
    ResponsiveTargetType,
    FixedTarget,
} from 'contexts/OnboardingContext/OnboardingContext.type';
import {
    isATextTarget,
    isAPositionTarget,
} from 'contexts/OnboardingContext/OnboardingContext.utils';
import { Box } from '@landr/maestro';
import { Breakpoints, Breakpoint } from '@landr/maestro-legacy';
import { cva, css } from '@landr/styled-system/css';
import { createContainer } from 'utils/createContainer';
import { isElementHasDimension } from 'utils/isElementHasDimension';
import { useMutationObserver } from 'hooks/useMutationObserver';
import useGetCurrentBreakpoint from 'hooks/useGetCurrentBreakpoint/useGetCurrentBreakpoint';

type BreakpointType = Breakpoint | 'default';

export const containerId = 'onboarding-container';

const queryByText = (target: TextTarget): Element | undefined => {
    return Array.from(document.querySelectorAll(target.parent)).find(
        (el) => isElementHasDimension(el) && el.textContent === target.text,
    );
};

const anchorFixedStyles = cva({
    base: {
        position: 'fixed',
        backgroundColor: 'red',
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        pointerEvents: 'none',
    },
    variants: {
        target: {
            pluginSync: { top: 0, left: 94 },
            pluginDrag: { bottom: 0, right: 28 },
            pluginGeneralSetting: {
                top: 0,
                right: 28,
                left: 'auto',
            },
        },
    },
});

const AnchorFixed = forwardRef<
    HTMLDivElement,
    React.PropsWithChildren<{ target: FixedTarget }>
>(({ children, target }, ref): JSX.Element => {
    return (
        <Box ref={ref} className={anchorFixedStyles({ target })}>
            {children}
        </Box>
    );
});

const anchorAbsoluteStyles = css({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    maxWidth: '100% !important',
});

const onboardingContainerId = 'onboarding-container';

export const getTarget = (
    target: TargetType,
): {
    targetElement: Element | undefined;
    Anchor: JSX.Element;
} => {
    let tentativeTarget = undefined;
    let tentativeAnchor = undefined;

    if (isAPositionTarget(target)) {
        tentativeTarget =
            document.querySelector(`#${onboardingContainerId}`) ||
            createContainer(onboardingContainerId);
        tentativeAnchor = <AnchorFixed target={target} />;
    } else if (typeof target === 'string') {
        tentativeTarget = Array.from(document.querySelectorAll(target)).find(
            (el) => isElementHasDimension(el),
        );
    } else if (isATextTarget(target)) {
        tentativeTarget = queryByText(target);
    }

    const targetElement = tentativeTarget;
    const Anchor = tentativeAnchor || <Box className={anchorAbsoluteStyles} />;

    return { targetElement, Anchor };
};

// This evalute current breakpoint and return the first valid target of a step
export const useResponsiveTarget = (
    target: ResponsiveTargetType | undefined,
): TargetType | undefined => {
    const breakpointsObject = {
        ...Breakpoints,
        default: 1000000,
        unknown: 1000000,
    };

    const currentBreakpoint = useGetCurrentBreakpoint();

    const [currentTargetBreakpoint, setCurrentTargetBreakpoint] =
        useState<BreakpointType>('default');

    useLayoutEffect(() => {
        if (currentBreakpoint && target) {
            const targetBreakpoint = Object.keys(target);

            // Filter breakpoint smaller than currentBreakpoint
            const filteredTargetBreakpoint = targetBreakpoint.filter(
                (breakpoint) => {
                    return (
                        breakpointsObject[breakpoint as BreakpointType] >=
                        breakpointsObject[currentBreakpoint]
                    );
                },
            );

            // Sort filtered breakpoint from smaller to bigger
            const sortedTargetBreakpoint = filteredTargetBreakpoint.sort(
                (a, b) => {
                    const breakpointA = breakpointsObject[a as BreakpointType];
                    const breakpointB = breakpointsObject[b as BreakpointType];

                    return breakpointA - breakpointB;
                },
            );

            setCurrentTargetBreakpoint(
                sortedTargetBreakpoint[0] as BreakpointType,
            );
        }
    }, [currentBreakpoint, target]);

    return target && target[currentTargetBreakpoint];
};

export const useGetTarget = (
    target: ResponsiveTargetType | undefined,
): {
    currentTarget: TargetType | undefined;
    targetElement: Element | undefined;
    Anchor: JSX.Element | undefined;
} => {
    const currentTarget = useResponsiveTarget(target);
    const isNotAPositionTarget = !isAPositionTarget(currentTarget);

    const [targetElement, setTargetElement] = useState<Element>();
    const [Anchor, setAnchor] = useState<JSX.Element>();

    useLayoutEffect(() => {
        if (currentTarget) {
            const { targetElement, Anchor } = getTarget(currentTarget);

            setTargetElement(targetElement);
            setAnchor(Anchor);
        }
    }, [currentTarget]);

    const mutationObserverCallback = useCallback(() => {
        if (currentTarget && isNotAPositionTarget) {
            const { targetElement, Anchor } = getTarget(currentTarget);

            setTargetElement(targetElement);
            setAnchor(Anchor);
        }
    }, [currentTarget, isNotAPositionTarget]);

    /**
     * Observe the entire document.body for mutation,
     * but the hook debounce the callback to mitigate performance load.
     * We had to observe the entire DOM because it was impossible to guarantee
     * that any parent of a target won't be remove at some point from the DOM, disconnecting the mutation observer.
     */
    useMutationObserver(document.body, mutationObserverCallback);

    return { currentTarget, targetElement, Anchor };
};
